import React, { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { FileText, CheckCircle, Users, TrendingUp, Clock, Package, AlertCircle } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${API_URL}/api/v1`;

interface DashboardMetric {
  label: string;
  value: number | string;
  color: string;
}

interface DashboardStats {
  role: string;
  metrics: DashboardMetric[];
}

const DashboardPage: React.FC = () => {
  const { user } = useAuth();
  const [stats, setStats] = useState<DashboardMetric[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboardStats();
  }, []);

  const fetchDashboardStats = async () => {
    try {
      setLoading(true);
      const response = await axios.get<DashboardStats>(`${API}/dashboard/stats`);
      setStats(response.data.metrics);
    } catch (error) {
      console.error('Failed to fetch dashboard stats:', error);
      // Fallback to empty stats
      setStats([]);
    } finally {
      setLoading(false);
    }
  };

  const getIcon = (index: number) => {
    const icons = [FileText, Clock, CheckCircle, Package, Users, TrendingUp, AlertCircle];
    return icons[index % icons.length];
  };

  return (
    <div data-testid="dashboard-page">
      {/* Welcome Card */}
      <div className="bg-white rounded-lg shadow-sm p-6 mb-6 border border-gray-200">
        <h1 className="text-2xl font-bold text-gray-800 mb-2" style={{ fontFamily: 'Inter, sans-serif' }}>
          Welcome back, {user?.name}!
        </h1>
        <p className="text-gray-600">Here's what's happening with your workflow today.</p>
      </div>

      {/* Stats Grid */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {stats.map((stat, index) => {
            const Icon = getIcon(index);
            
            // Define gradient backgrounds based on color
            const gradientMap: Record<string, string> = {
              'bg-blue-500': 'bg-gradient-to-br from-blue-400 via-blue-500 to-blue-600',
              'bg-yellow-500': 'bg-gradient-to-br from-yellow-400 via-yellow-500 to-orange-500',
              'bg-green-500': 'bg-gradient-to-br from-green-400 via-green-500 to-emerald-600',
              'bg-red-500': 'bg-gradient-to-br from-red-400 via-red-500 to-rose-600',
              'bg-purple-500': 'bg-gradient-to-br from-purple-400 via-purple-500 to-indigo-600',
              'bg-gray-500': 'bg-gradient-to-br from-gray-400 via-gray-500 to-slate-600'
            };
            
            const gradientClass = gradientMap[stat.color] || 'bg-gradient-to-br from-blue-400 to-blue-600';
            
            return (
              <div
                key={`${stat.label}-${index}`}
                className={`${gradientClass} rounded-xl shadow-lg p-6 hover:shadow-xl transform hover:-translate-y-1 transition-all duration-300`}
              >
                <div className="flex items-center justify-between mb-4">
                  <div className="bg-white/20 backdrop-blur-sm p-3 rounded-lg">
                    <Icon className="text-white" size={28} />
                  </div>
                  <div className="bg-white/20 backdrop-blur-sm px-3 py-1 rounded-full">
                    <span className="text-xs font-semibold text-white">Live</span>
                  </div>
                </div>
                <div>
                  <p className="text-white/90 text-sm mb-2 font-medium">{stat.label || 'N/A'}</p>
                  <p className="text-4xl font-bold text-white drop-shadow-lg">{stat.value}</p>
                </div>
                {/* Decorative element */}
                <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full -mr-16 -mt-16 blur-2xl"></div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default DashboardPage;